'use client';


import { getAsset } from './assetStore';
import { browserWindow } from './browserSurface';

const UI_SOUNDS = new Set(['click', 'buttonclickrelease', 'menu_select', 'achievement_unlock', 'earnings', 'click_wheel', 'item_drop', 'upgrade_fail', 'jackpot', 'no_profit', 'killchicken']);
export const UI_SOUND_NAMES: readonly string[] = [...UI_SOUNDS];
const ROULETTE_SOUNDS = new Set(['cylinder-spin', 'dry-fire', 'gunshot', 'bullet-load', 'cylinder-close', 'casing-drop']);
// WHY: ?v is the cache key: file paths are stable, so a changed sound without a bump is served stale by browsers/CDN
const SOUND_VERSION = 3;
const url = (name: string) =>
  ROULETTE_SOUNDS.has(name)
    ? `/roulette/${name}.mp3?v=${SOUND_VERSION}`
    : `${UI_SOUNDS.has(name) ? '/sounds' : '/battle'}/${name}.mp3?v=${SOUND_VERSION}`;

export const soundUrl = (name: string): string => url(name);

let ctx: AudioContext | null = null;
const buffers: Record<string, AudioBuffer> = {};
const raw: Record<string, ArrayBuffer> = {};
const loading = new Set<string>();
let unlockWired = false;

const ALL_MUTE_KEY = 'sound:all-muted';
let allMuted = false;
try { allMuted = typeof window !== 'undefined' && window.localStorage.getItem(ALL_MUTE_KEY) === '1'; } catch { }
const allMuteListeners = new Set<() => void>();
let masterGain: GainNode | null = null;

export function isAllMuted(): boolean { return allMuted; }
export function subscribeAllMuted(cb: () => void): () => void {
  allMuteListeners.add(cb);
  return () => allMuteListeners.delete(cb);
}
export function setAllMuted(next: boolean): void {
  if (allMuted === next) return;
  allMuted = next;
  try { window.localStorage.setItem(ALL_MUTE_KEY, next ? '1' : '0'); } catch { }
  if (masterGain) masterGain.gain.value = next ? 0 : 1;
  allMuteListeners.forEach((cb) => cb());
}

// WHY: decodeAudioData detaches its input — decode a COPY and keep `raw` for re-decoding into a recreated context
function redecodeAll(c: AudioContext): void {
  for (const name of Object.keys(raw)) {
    void c.decodeAudioData(raw[name].slice(0)).then((buf) => { buffers[name] = buf; }).catch(() => undefined);
  }
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  // WHY: mobile Safari/Telegram WebView return a backgrounded context CLOSED; resume() on it throws forever — rebuild it
  if (ctx && ctx.state === 'closed') { ctx = null; silentEl = null; silentNode = null; masterGain = null; }
  if (!ctx) {
     
    const w = browserWindow();
    const AC = w.AudioContext ?? w.webkitAudioContext;
     
    if (!AC) return null;
    try { ctx = new AC(); } catch { return null; }
    masterGain = ctx.createGain();
    masterGain.gain.value = allMuted ? 0 : 1;
    masterGain.connect(ctx.destination);
    redecodeAll(ctx);
  }
  return ctx;
}

export function wakeAudio(): void {
  const c = getCtx();
  if (!c) return;
  resumeCtx(c);
  if (c.state !== 'running') {
    window.setTimeout(() => { const cc = getCtx(); if (cc) resumeCtx(cc); }, 250);
  }
  enableMuteBypass();
}

// WHY: not gated on 'suspended': iOS brings the context back as non-standard 'interrupted' after background
function resumeCtx(c: AudioContext): void {
  if (c.state !== 'running') void c.resume().catch(() => undefined);
}

export function wireUnlock(): void {
  if (unlockWired || typeof window === 'undefined') return;
  unlockWired = true;
  window.addEventListener('pointerdown', wakeAudio);
  window.addEventListener('touchend', wakeAudio);
  window.addEventListener('keydown', wakeAudio);
}


// WHY: iOS routes a bare AudioContext to the ringer channel (mute switch); a silent <audio> via createMediaElementSource
// moves it to media
const repeat = (n: number, s: string): string => { let out = s; for (; n > 1; n--) out += s; return out; };

const SILENCE =
  'data:audio/mpeg;base64,//uQx' + repeat(23, 'A') +
  'WGluZwAAAA8AAAACAAACcQCA' + repeat(16, 'gICA') + repeat(66, '/') +
  '8AAABhTEFNRTMuMTAwA8MAAAAAAAAAABQgJAUHQQAB9AAAAnGMHkkI' + repeat(320, 'A') +
  '//sQxAADgnABGiAAQBCqgCRMAAgEAH' + repeat(15, '/') +
  '7+n/9FTuQsQH//////2NG0jWUGlio5gLQTOtIoeR2WX////X4s9Atb/JRVCbBUpeRUq' + repeat(18, '/') +
  '9RUi0f2jn/+xDECgPCjAEQAABN4AAANIAAAAQVTEFNRTMuMTAw' + repeat(97, 'V') + 'Q==';

let silentEl: HTMLAudioElement | null = null;
let silentNode: MediaElementAudioSourceNode | null = null;

export function enableMuteBypass(): void {
  if (typeof document === 'undefined' || document.hidden) return;
  const c = getCtx();
  if (!c) return;
  resumeCtx(c);
  if (!silentEl) {
    silentEl = document.createElement('audio');
    silentEl.loop = true;
    silentEl.preload = 'auto';
    silentEl.disableRemotePlayback = true;
    silentEl.setAttribute('x-webkit-airplay', 'deny');
    silentEl.src = SILENCE;
    // WHY: createMediaElementSource is one-shot per element — bind once and keep it
    try {
      silentNode = c.createMediaElementSource(silentEl);
      silentNode.connect(c.destination);
    } catch { }
  }
  if (silentEl.paused) {
    Promise.resolve(silentEl.play()).catch(() => undefined);
  }
}

export function disableMuteBypass(): void {
  if (silentEl && !silentEl.paused) {
    try { silentEl.pause(); } catch { }
  }
}


async function loadBuffer(name: string): Promise<void> {
  const c = getCtx();
  if (!c) throw new Error('Web Audio API is unavailable');
  if (buffers[name] || loading.has(name)) return;
  loading.add(name);
  try {
    const blob = await getAsset(url(name));
    const arr = await blob.arrayBuffer();
    raw[name] = arr.slice(0);
    buffers[name] = await c.decodeAudioData(arr);
  } finally {
    loading.delete(name);
  }
}

export async function loadSounds(names: readonly string[], onUnit?: () => void): Promise<void> {
  wireUnlock();
  const c = getCtx();
  if (!c) throw new Error('Web Audio API is unavailable');
  await Promise.all(names.map((n) => loadBuffer(n).then(() => { try { onUnit?.(); } catch { } })));
}

export const isLoaded = (name: string): boolean => !!buffers[name];

export function playSfx(name: string, volume = 1): void {
  if (allMuted) return; // global mute — skip spawning a one-shot entirely
  const c = getCtx();
  const buf = buffers[name];
  if (!c || !buf) return;
  resumeCtx(c);
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = volume;
    src.connect(g).connect(masterGain ?? c.destination);
    src.start();
  } catch { }
}

export async function playLoop(name: string, volume = 0.35): Promise<() => void> {
  const c = getCtx();
  if (!c) return () => undefined;
  if (!buffers[name]) {
    try { await loadBuffer(name); } catch { return () => undefined; }
  }
  const buf = buffers[name];
  if (!buf) return () => undefined;
  resumeCtx(c);
  let src: AudioBufferSourceNode | null = null;
  let gain: GainNode | null = null;
  try {
    src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    gain = c.createGain();
    gain.gain.value = volume;
    src.connect(gain).connect(masterGain ?? c.destination);
    src.start();
  } catch { return () => undefined; }
  return () => {
    try { src.stop(); } catch { }
    try { src.disconnect(); } catch { }
    try { gain.disconnect(); } catch { }
  };
}
